import ScheduleTimeRows from './ScheduleTimeRows';
import {
  MonthlySchedule,
  MonthlySchedulerProps,
  MonthlyTrigger,
  MissingDayPolicy,
  QuarterMonth,
  WeekdayOrdinal,
} from './CalendarSchedulerTypes';
import { ScheduleInterval } from './WorkSchedulerTypes';
import {
  DEFAULT_TIME,
  ORDINALS,
  WEEKDAYS,
  orderedSlotKeys,
  quarterMonths,
  slotLabel,
  switchTrigger,
} from '../utils/calendarSchedule';
import './WorkScheduler.css';
import './CalendarScheduler.css';

const TRIGGERS: { value: MonthlyTrigger; label: string }[] = [
  { value: 'date', label: 'On date' },
  { value: 'weekday', label: 'On weekday' },
  { value: 'business_day', label: 'Business day' },
];

const BUSINESS_DAYS = [
  { key: 'first', label: 'First business day' },
  { key: 'last', label: 'Last business day' },
];

const QUARTER_MONTHS: { value: QuarterMonth; label: string }[] = [
  { value: 1, label: '1st month' },
  { value: 2, label: '2nd month' },
  { value: 3, label: '3rd month' },
];

const QUARTER_SHORTCUTS: { label: string; quarterMonth: QuarterMonth; dayKey: string }[] = [
  { label: 'Quarter start', quarterMonth: 1, dayKey: '1' },
  { label: 'Quarter end', quarterMonth: 3, dayKey: 'last' },
];

const MISSING_DAY_OPTIONS: { value: MissingDayPolicy; label: string }[] = [
  { value: 'last_day', label: 'Run on last day' },
  { value: 'skip', label: 'Skip that month' },
];

const DATE_KEYS = [...Array.from({ length: 31 }, (_, i) => String(i + 1)), 'last'];

function MonthlyScheduler({ schedule, setSchedule, isDisabled = false }: MonthlySchedulerProps) {
  const slotKeys = orderedSlotKeys(schedule);
  const update = (partial: Partial<MonthlySchedule>) => setSchedule({ ...schedule, ...partial });

  const setSlots = (key: string, intervals: ScheduleInterval[]) =>
    update({ slots: { ...schedule.slots, [key]: intervals } });

  const toggleSlot = (key: string) => {
    if (schedule.slots[key]) {
      const { [key]: _removed, ...rest } = schedule.slots;
      update({ slots: rest });
    } else {
      setSlots(key, [{ start: DEFAULT_TIME }]);
    }
  };

  const applyShortcut = (quarterMonth: QuarterMonth, dayKey: string) =>
    setSchedule({
      ...switchTrigger(schedule, 'date'),
      quarter_month: quarterMonth,
      slots: { [dayKey]: [{ start: DEFAULT_TIME }] },
    });

  const hasShortMonthDay = slotKeys.some((k) => k !== 'last' && Number(k) > 28);

  return (
    <div className="cs-root">
      {schedule.repeat === 'quarterly' && (
        <>
          <div className="cs-caption">Month of quarter</div>
          <div className="cs-quarter-cards">
            {QUARTER_MONTHS.map((qm) => (
              <button
                key={qm.value}
                type="button"
                className={`cs-quarter-card ${schedule.quarter_month === qm.value ? 'cs-quarter-card--active' : ''}`}
                onClick={() => update({ quarter_month: qm.value })}
                disabled={isDisabled}
              >
                <span className="cs-quarter-card-title">{qm.label}</span>
                <span className="cs-quarter-card-months">
                  {quarterMonths(qm.value).join(' · ')}
                </span>
              </button>
            ))}
          </div>
          <div className="cs-inline-row cs-shortcuts">
            <span className="cs-muted">Shortcuts</span>
            {QUARTER_SHORTCUTS.map((shortcut) => (
              <button
                key={shortcut.label}
                type="button"
                className="cs-chip"
                onClick={() => applyShortcut(shortcut.quarterMonth, shortcut.dayKey)}
                disabled={isDisabled}
              >
                {shortcut.label}
              </button>
            ))}
          </div>
        </>
      )}

      <div className="cs-inline-row">
        {TRIGGERS.map((t) => (
          <button
            key={t.value}
            type="button"
            className={`cs-chip ${schedule.trigger === t.value ? 'cs-chip--active' : ''}`}
            onClick={() => setSchedule(switchTrigger(schedule, t.value))}
            disabled={isDisabled}
          >
            {t.label}
          </button>
        ))}
      </div>

      {schedule.trigger === 'date' && (
        <>
          <div className="cs-date-grid">
            {DATE_KEYS.map((key) => (
              <button
                key={key}
                type="button"
                className={`ws-day-pill ${schedule.slots[key] ? 'ws-day-pill--active' : ''} ${key === 'last' ? 'cs-date-pill--last' : ''}`}
                onClick={() => toggleSlot(key)}
                disabled={isDisabled}
              >
                {key === 'last' ? 'Last' : key}
              </button>
            ))}
          </div>
          {hasShortMonthDay && (
            <div className="cs-inline-row cs-missing-day">
              <span>In months without that day:</span>
              {MISSING_DAY_OPTIONS.map((option) => (
                <label key={option.value} className="cs-radio">
                  <input
                    type="radio"
                    name="cs-missing-day"
                    checked={(schedule.missing_day ?? 'last_day') === option.value}
                    onChange={() => update({ missing_day: option.value })}
                    disabled={isDisabled}
                  />
                  {option.label}
                </label>
              ))}
            </div>
          )}
        </>
      )}

      {schedule.trigger === 'weekday' && (
        <div className="cs-inline-row cs-weekday-row">
          <span>On the</span>
          <select
            className="cs-select"
            value={schedule.ordinal ?? 'first'}
            onChange={(e) => update({ ordinal: e.target.value as WeekdayOrdinal })}
            disabled={isDisabled}
          >
            {ORDINALS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <div className="ws-day-pills">
            {WEEKDAYS.map((day) => (
              <button
                key={day.key}
                type="button"
                className={`ws-day-pill ${schedule.slots[day.key] ? 'ws-day-pill--active' : ''}`}
                onClick={() => toggleSlot(day.key)}
                disabled={isDisabled}
              >
                {day.short}
              </button>
            ))}
          </div>
        </div>
      )}

      {schedule.trigger === 'business_day' && (
        <div className="cs-inline-row">
          {BUSINESS_DAYS.map((b) => (
            <button
              key={b.key}
              type="button"
              className={`cs-chip ${schedule.slots[b.key] ? 'cs-chip--active' : ''}`}
              onClick={() => toggleSlot(b.key)}
              disabled={isDisabled}
            >
              {b.label}
            </button>
          ))}
        </div>
      )}

      <ScheduleTimeRows
        rows={slotKeys.map((key) => ({
          key,
          label: slotLabel(schedule, key),
          intervals: schedule.slots[key],
        }))}
        isDisabled={isDisabled}
        onTimeChange={(key, idx, _field, value) =>
          setSlots(
            key,
            schedule.slots[key].map((it, i) => (i === idx ? { start: value } : it)),
          )
        }
        onAdd={(key) => setSlots(key, [...schedule.slots[key], { start: DEFAULT_TIME }])}
        onRemove={(key, idx) =>
          setSlots(
            key,
            schedule.slots[key].filter((_, i) => i !== idx),
          )
        }
      />
    </div>
  );
}

export default MonthlyScheduler;
